/**
 * Status and balance badges. One definition, used on every screen and in the
 * portal, so a member never looks "active" in one place and "expired" in
 * another.
 */
import { formatEgp } from '@shared/money';
import { STATUS_CLASSES, STATUS_LABELS } from '@shared/pricing';
import type { SubscriptionStatus } from '@shared/types';

export function StatusBadge({
  status,
  size = 'md',
}: {
  status: SubscriptionStatus;
  size?: 'sm' | 'md';
}) {
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-btn border font-bold uppercase tracking-button ${
        STATUS_CLASSES[status]
      } ${size === 'sm' ? 'px-2 py-1 text-[10px]' : 'px-3 py-1.5 text-xs'}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

/** The amber "Balance due" badge, shown everywhere money is still owed. */
export function BalanceDueBadge({
  remaining,
  size = 'md',
}: {
  remaining: number;
  size?: 'sm' | 'md';
}) {
  if (remaining <= 0) return null;
  return (
    <span
      className={`tabular inline-block whitespace-nowrap rounded-btn border
                  border-status-due/40 bg-status-dueBg font-bold uppercase
                  tracking-button text-status-due ${
                    size === 'sm' ? 'px-2 py-1 text-[10px]' : 'px-3 py-1.5 text-xs'
                  }`}
    >
      Due {formatEgp(remaining, { hideZeroFraction: true })}
    </span>
  );
}

/** Shows when a paused membership resumes. */
export function FrozenBadge({ until, size = 'md' }: { until: string; size?: 'sm' | 'md' }) {
  return (
    <span
      className={`tabular inline-block whitespace-nowrap rounded-btn border
                  border-status-frozen/40 bg-status-frozenBg font-bold uppercase
                  tracking-button text-status-frozen ${
                    size === 'sm' ? 'px-2 py-1 text-[10px]' : 'px-3 py-1.5 text-xs'
                  }`}
    >
      Frozen to {until}
    </span>
  );
}

/** Copies a phone number, with a brief confirmation. For follow-up calls. */
export function CopyPhoneButton({ phone }: { phone: string }) {
  return (
    <button
      type="button"
      onClick={async (e) => {
        const btn = e.currentTarget;
        try {
          await navigator.clipboard.writeText(phone);
          const original = btn.textContent;
          btn.textContent = 'Copied';
          setTimeout(() => {
            btn.textContent = original;
          }, 1200);
        } catch {
          // Clipboard can be blocked; the number is visible on the row anyway.
        }
      }}
      className="min-h-[40px] whitespace-nowrap rounded-btn border border-edge px-3 text-xs
                 font-bold uppercase tracking-button text-white/60
                 transition-all duration-brand hover:border-brand hover:text-paper"
      title={`Copy ${phone}`}
    >
      Copy
    </button>
  );
}
